import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogPostLayout from '../../components/redesign/BlogPostLayout.jsx';
import setSeo from '../../utils/seo.js';

const heroImage = '/openclaw-suitecrm-hero.svg';
const ogImage = '/blog-openclaw-docker-og.png';
const loomEmbedUrl = 'https://www.loom.com/embed/f5fb7c5273814a5c93590e98abbc3565';

export default function OpenClawSuiteCRMIntegration() {
  useEffect(() => {
    const canonicalUrl = `${window.location.origin}/blog/openclaw-suitecrm-integration`;
    setSeo({
      title: 'SuiteCRM + OpenClaw in One Docker Command | Aakeeo',
      description:
        'Self-host SuiteCRM 8 with the OpenClaw AI agent skill in one docker compose up. Public GHCR images, 3 worked examples, MIT-licensed repo.',
      keywords: [
        'SuiteCRM OpenClaw Docker',
        'SuiteCRM docker compose',
        'self-hosted SuiteCRM',
        'OpenClaw SuiteCRM integration',
        'OpenClaw SuiteCRM skills',
        'OpenClaw custom skills',
        'OpenClaw CRM integration',
        'SuiteCRM AI integration',
        'secure AI agents',
        'self-hosted AI',
      ],
      canonical: canonicalUrl,
      og: {
        title: 'SuiteCRM + OpenClaw in One Docker Command',
        description: 'Self-host SuiteCRM 8 with the OpenClaw AI agent skill in one docker compose up. Public GHCR images, 3 worked examples, MIT-licensed.',
        url: canonicalUrl,
        image: window.location.origin + ogImage,
        type: 'article',
      },
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'SuiteCRM + OpenClaw in One Docker Command',
          image: 'https://www.aakeeo.com' + ogImage,
          url: canonicalUrl,
          mainEntityOfPage: canonicalUrl,
          datePublished: '2026-02-13',
          dateModified: '2026-04-24',
          wordCount: 3150,
          author: { '@type': 'Organization', name: 'Aakeeo Technologies' },
          publisher: {
            '@type': 'Organization',
            name: 'Aakeeo Technologies',
            logo: { '@type': 'ImageObject', url: 'https://www.aakeeo.com/Aakeeo_favicon.png' },
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.aakeeo.com/' },
            { '@type': 'ListItem', position: 2, name: 'Resources', item: 'https://www.aakeeo.com/resources' },
            { '@type': 'ListItem', position: 3, name: 'SuiteCRM + OpenClaw in One Docker Command' },
          ],
        },
      ],
    });
  }, []);

  return (
    <BlogPostLayout
      title="OpenClaw + SuiteCRM: secure AI agents without giving away your identity."
      category="AI & Automation"
      date="Feb 13, 2026"
      readTime="12 min"
      image={heroImage}
      excerpt="A secure, self-hosted integration blueprint for OpenClaw + SuiteCRM — strict boundaries, API-scoped permissions, event-driven automation."
    >
      <h2>Run it yourself</h2>
      <p>One command, local SuiteCRM 8 with the OpenClaw agent skill pre-wired and three worked examples ready to run:</p>
      <pre><code>{`git clone https://github.com/Aakeeo/suitecrm-openclaw
cd suitecrm-openclaw
cp .env.example .env
docker compose up -d`}</code></pre>
      <p>SuiteCRM lands at <code>http://localhost:8080</code>. Or skip the build and pull the prebuilt images direct from GHCR:</p>
      <pre><code>{`docker pull ghcr.io/aakeeo/suitecrm-app:0.1.0
docker pull ghcr.io/aakeeo/openclaw-suitecrm-agent:0.1.0`}</code></pre>
      <p>
        Full repo, README, and release notes: <a href="https://github.com/Aakeeo/suitecrm-openclaw" target="_blank" rel="noopener noreferrer">github.com/Aakeeo/suitecrm-openclaw</a>. MIT-licensed, v0.1.0 shipped April 2026. Everything below is the architecture and the reasoning behind what you just booted.
      </p>

      <p>
        <strong>Most AI agent demos are impressive and terrifying in equal measure.</strong> The agent reads your inbox, rearranges your calendar, and accesses files across your system. In a personal context that&apos;s convenient. In a business handling customer financial data, patient records, or sales pipelines — it&apos;s a compliance nightmare.
      </p>
      <p>
        We took a different approach. Instead of giving an AI agent broad access to personal systems, we integrated <Link to="/services/ai-workflow-automation">OpenClaw directly with SuiteCRM</Link> — keeping automation inside the business boundary, with explicit permissions, minimal surface area, and zero identity-level access.
      </p>
      <p>This post is the full architecture breakdown. If you&apos;re evaluating AI agents for your CRM, this is the security-first approach we recommend to every client.</p>

      <div className="callout">
        <p><strong>Looking to add AI to your SuiteCRM?</strong> We build secure AI automation — lead scoring, auto follow-ups, and intelligent workflows on your infrastructure. <Link to="/services/ai-workflow-automation">Explore AI automation services →</Link></p>
      </div>

      <h2>Why integrate with SuiteCRM (and not Salesforce or HubSpot)</h2>
      <p><Link to="/opensource">SuiteCRM</Link> is a structured, self-hosted business system. It already contains:</p>
      <ul>
        <li>Leads, Contacts, Accounts</li>
        <li>Opportunities, Tasks, Cases</li>
        <li>Activities — calls, meetings, notes</li>
      </ul>
      <p>Defined workflows. A clean API. Instead of giving OpenClaw access to personal systems, we integrated it directly with SuiteCRM, keeping automation inside the business boundary.</p>
      <blockquote>The principle: agents should automate systems, not own identities.</blockquote>

      <h2>Architecture overview: OpenClaw + SuiteCRM on a VPS</h2>
      <p>The integration runs entirely self-hosted.</p>

      <h3>Infrastructure</h3>
      <ul>
        <li>VPS-hosted SuiteCRM (system of record)</li>
        <li>VPS-hosted OpenClaw gateway</li>
        <li>Custom SuiteCRM skill</li>
        <li>Webhook integration between SuiteCRM and OpenClaw</li>
        <li>No third-party marketplace skills</li>
        <li>No personal inbox or calendar access</li>
      </ul>

      <h3>Design goals</h3>
      <ul>
        <li>Clear API boundary</li>
        <li>Minimal permission scope</li>
        <li>Controlled automation surface</li>
        <li>No identity-level agent access</li>
        <li>Event-driven communication</li>
      </ul>
      <p>The CRM remains authoritative. OpenClaw becomes an intelligent interface layer.</p>

      <h2>How the integration works</h2>

      <h3>1. Custom SuiteCRM skill</h3>
      <p>While a default SuiteCRM skill exists, we built a minimal custom skill for better control. It handles: list contacts, create contact, fetch account details, controlled CRUD, explicit API calls to SuiteCRM v8 JSON API. Each action is clearly defined. Smaller surface = stronger security.</p>

      <h3>2. SuiteCRM API access</h3>
      <ul>
        <li>SuiteCRM v8 REST API</li>
        <li>OAuth2 authentication</li>
        <li>Token refresh handling</li>
        <li>Strict endpoint scoping</li>
      </ul>
      <p>OpenClaw only talks to SuiteCRM&apos;s API — not the database directly, not unrelated services. Upgrade-safe and maintainable.</p>

      <h3>3. Webhook integration</h3>
      <p>We also connected SuiteCRM events to the OpenClaw gateway using webhooks — the same <Link to="/services/crm-integration">event-driven integration pattern</Link> we use across all CRM projects. Enables: new-lead notification, case-update action, opportunity-stage triggered workflow. Instead of polling, the system is event-driven.</p>

      <h2>What the demo shows</h2>
      {loomEmbedUrl && (
        <div className="articleA-video">
          <iframe
            src={loomEmbedUrl}
            title="OpenClaw SuiteCRM Integration Demo"
            frameBorder="0"
            allowFullScreen
          />
        </div>
      )}
      <ul>
        <li>List contacts from SuiteCRM via OpenClaw</li>
        <li>Create a new contact using conversational input</li>
        <li>Execute CRM actions without UI interaction</li>
        <li>Maintain SuiteCRM as the system of record</li>
      </ul>
      <p>The CRM doesn&apos;t disappear. The interface does.</p>

      <h2>Why we avoided identity-level access</h2>
      <p>Many OpenClaw demos focus on personal automation: inbox reading, calendar scheduling, file access, personal system control. In a business context, AI agents should operate within structured systems, defined workflows, explicit permissions and measurable automation zones — not personal identity layers.</p>
      <p>This separation creates:</p>
      <ul>
        <li>Better security</li>
        <li>Cleaner audit trails</li>
        <li>Clear responsibility boundaries</li>
        <li>Reduced compliance exposure</li>
        <li>Predictable behaviour</li>
      </ul>

      <h2>Security considerations</h2>
      <ul>
        <li>Self-host OpenClaw</li>
        <li>Disable marketplace skill installation</li>
        <li>Load only local custom skills</li>
        <li>Restrict OpenClaw network access</li>
        <li>Use API-level authentication only</li>
        <li>Protect webhook endpoints with tokens</li>
        <li>Avoid granting file-system or personal-system access</li>
      </ul>
      <p>Agents are powerful. But architecture determines whether that power is stable or chaotic.</p>

      <h2>Skills to build first</h2>
      <ul>
        <li>Read-only contact and account lookup skill</li>
        <li>Controlled create/update contact skill with field validation</li>
        <li>Lead triage skill triggered from webhook events</li>
        <li>Case summary skill that writes back structured notes</li>
      </ul>
      <p>This phased approach keeps the integration secure while still delivering visible operational gains.</p>

      <h2>The broader shift: conversational CRM</h2>
      <p>This integration isn&apos;t about adding AI features to CRM. It&apos;s about converting intent into structured CRM actions, reducing UI dependency, keeping automation within business boundaries, preserving system integrity.</p>
      <p>The future of CRM is not more dashboards. It is structured systems responding to intent.</p>

      <h2>Final thought</h2>
      <p>OpenClaw + SuiteCRM works best when: the CRM stays the system of record, the agent stays scoped, permissions stay minimal, identity stays protected.</p>
      <p>AI should sit between intent and execution — not between you and your identity.</p>

      <div className="callout">
        <p>
          Want this architecture for your CRM? We&apos;ve built secure AI integrations across healthcare, finance and logistics. <Link to="/contact">Book a free CRM audit</Link> and we&apos;ll map what&apos;s possible for your setup.
        </p>
      </div>
    </BlogPostLayout>
  );
}
